import { Minus, Plus } from "lucide-react";

/**
 * A zoom between fixed stops (Tyler, 2026-10-05): a slider with a - button at one end and a + at the
 * other, a notch under the track at each stop, and the stop it is on named beside it. The tank schedule
 * zooms through how much time fits across; the Brewery layout through how many squares across it is.
 * Stops before `min` cannot be chosen.
 */
export function ZoomSlider<T extends string | number>({
  stops,
  value,
  onZoom,
  labelOf,
  name,
  less,
  more,
  min = 0,
}: {
  stops: readonly T[];
  value: T;
  onZoom: (stop: T) => void;
  /** What a stop is called, beside the slider and on its notch. */
  labelOf: (stop: T) => string;
  /** What the slider sets, for a screen reader. */
  name: string;
  /** What the - and + buttons do. */
  less: string;
  more: string;
  /** The first stop that can be chosen. */
  min?: number;
}) {
  const i = Math.max(0, stops.indexOf(value));
  const last = stops.length - 1;
  const to = (n: number) => {
    const stop = stops[Math.min(last, Math.max(min, n))];
    if (stop !== undefined && stop !== value) onZoom(stop);
  };
  return (
    <div className="ts-zoom">
      <button
        type="button"
        className="icon-button"
        aria-label={less}
        title={less}
        disabled={i <= min}
        onClick={() => to(i - 1)}
      >
        <Minus size={16} />
      </button>
      <div className="ts-zoom-track">
        <input
          type="range"
          min={0}
          max={last}
          step={1}
          value={i}
          aria-label={name}
          aria-valuetext={labelOf(value)}
          onChange={(e) => to(Number(e.target.value))}
        />
        <div className="ts-notches" aria-hidden>
          {stops.map((s, n) => (
            <span
              key={s}
              className={`${n === i ? "on" : ""} ${n < min ? "off" : ""}`}
              style={{ left: `calc(8px + (100% - 16px) * ${n / last})` }}
              title={labelOf(s)}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        className="icon-button"
        aria-label={more}
        title={more}
        disabled={i >= last}
        onClick={() => to(i + 1)}
      >
        <Plus size={16} />
      </button>
      <span className="ts-zoom-label">{labelOf(value)}</span>
    </div>
  );
}
